/**
 * パイプライン API クライアント
 *
 * バックエンド /api/v1/pipeline/* との通信。axios client (api) 経由で
 * Cognito JWT の付与と 401 ハンドリングを共通化している。S3 への presigned PUT
 * のみ axios を経由せず素の XMLHttpRequest で送る（Authorization ヘッダを付けない
 * ようにするのと、進捗計測のため）。
 */

import { api } from './client';
import type {
  PipelineJobResponse,
  PipelineJobListResponse,
  PipelineStartResponse,
  PipelineSubmitRequest,
  PipelineSubmitResponse,
  SectionSubmitRequest,
} from '@/types/pipeline';

/** ジョブ投入 + Presigned URL 取得 */
export async function submitPipelineJob(
  request: PipelineSubmitRequest,
): Promise<PipelineSubmitResponse> {
  const resp = await api.post<PipelineSubmitResponse>('/pipeline/submit', request);
  return resp.data;
}

/**
 * Presigned URL に音声ファイルを PUT アップロード
 *
 * 認可: presigned URL 自体に署名が含まれるため Authorization ヘッダ等は付けない。
 * 進捗計測のため XMLHttpRequest を直接使用する。
 */
export function uploadAudioToS3(
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'audio/wav');

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed: HTTP ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('S3 upload network error'));
    xhr.send(file);
  });
}

/** パイプライン処理開始 */
export async function startPipeline(jobId: string): Promise<PipelineStartResponse> {
  const resp = await api.post<PipelineStartResponse>(`/pipeline/jobs/${jobId}/start`);
  return resp.data;
}

/** ジョブ状態取得（ポーリング用） */
export async function getJobStatus(jobId: string): Promise<PipelineJobResponse> {
  const resp = await api.get<PipelineJobResponse>(`/pipeline/jobs/${jobId}`);
  return resp.data;
}

/** ジョブ一覧 */
export async function listJobs(clientName?: string): Promise<PipelineJobListResponse> {
  const params = clientName ? { client_name: clientName } : undefined;
  const resp = await api.get<PipelineJobListResponse>('/pipeline/jobs', { params });
  return resp.data;
}

/** 文字起こし結果（Gemini 補正済み JSON） */
export async function getTranscription(jobId: string): Promise<Record<string, unknown>> {
  const resp = await api.get<Record<string, unknown>>(`/pipeline/jobs/${jobId}/transcription`);
  return resp.data;
}

/** 評価結果（direct_evaluation.json） */
export async function getEvaluation(jobId: string): Promise<Record<string, unknown>> {
  const resp = await api.get<Record<string, unknown>>(`/pipeline/jobs/${jobId}/evaluation`);
  return resp.data;
}

/** 失敗ジョブのリトライ */
export async function retryJob(jobId: string): Promise<PipelineStartResponse> {
  const resp = await api.post<PipelineStartResponse>(`/pipeline/jobs/${jobId}/retry`);
  return resp.data;
}

/** セクション設定を確定して評価開始 */
export async function submitSections(
  jobId: string,
  sections: SectionSubmitRequest['sections'],
): Promise<PipelineStartResponse> {
  const resp = await api.post<PipelineStartResponse>(`/pipeline/jobs/${jobId}/sections`, {
    sections,
  });
  return resp.data;
}
